import { useEffect, useState } from 'react';

/**
 * useKeyboardOpen — BULLETPROOF v3
 *
 * Detects soft keyboard open across Android Capacitor WebView / iOS WKWebView / Web
 * using THREE independent signals so we can never miss a keyboard event:
 *   1) focusin/focusout on text inputs — fires INSTANTLY when user taps input
 *   2) window.innerHeight delta vs initial — works perfectly on Android adjustResize
 *      mode where VisualViewport.height ALSO shrinks (so the standard kbHeight
 *      calculation returns 0 — that's the bug that broke v2.13.8-12 on Android)
 *   3) VisualViewport offset/scroll — fallback for iOS WKWebView adjustNothing mode
 *
 * Writes `--kb-h` CSS variable + toggles `html.kb-open` class.
 *
 * NOTE: The PRIMARY hide mechanism in v2.13.13+ is a pure CSS `:has(input:focus)`
 * rule in App.css that requires no JS at all. This hook is now defense-in-depth
 * for ancient browsers without :has() support and to expose `--kb-h` for components
 * that want explicit keyboard-aware padding.
 */
const TEXT_INPUT_SELECTOR = 'input:not([type="button"]):not([type="submit"]):not([type="checkbox"]):not([type="radio"]):not([type="range"]):not([data-kb-no-hide]), textarea, [contenteditable="true"]';

export default function useKeyboardOpen() {
  const [state, setState] = useState({ open: false, height: 0 });

  useEffect(() => {
    const root = document.documentElement;

    // Capture initial viewport height (before any keyboard ever opens). On Android
    // with adjustResize, this is the full WebView height. We compare current height
    // against this to detect keyboard shrinkage.
    let INITIAL_HEIGHT = window.innerHeight;
    // Update INITIAL_HEIGHT on orientation change so we don't stay stuck after rotation
    const onOrientation = () => {
      // Only update if no input is focused (i.e., keyboard not active)
      const active = document.activeElement;
      const isInputFocused = active && active.matches && active.matches(TEXT_INPUT_SELECTOR);
      if (!isInputFocused) {
        setTimeout(() => { INITIAL_HEIGHT = window.innerHeight; }, 300);
      }
    };
    window.addEventListener('orientationchange', onOrientation);

    let viewportOpen = false;
    let focusOpen = false;
    let lastHeight = 0;

    const apply = () => {
      const isOpen = viewportOpen || focusOpen;
      root.style.setProperty('--kb-h', `${lastHeight}px`);
      root.classList.toggle('kb-open', isOpen);
      setState({ open: isOpen, height: lastHeight });
    };

    // Signal 1: window.innerHeight delta — works on Android adjustResize mode
    const onResize = () => {
      const delta = INITIAL_HEIGHT - window.innerHeight;
      if (delta > 100) {
        lastHeight = delta;
        viewportOpen = true;
      } else {
        lastHeight = 0;
        viewportOpen = false;
      }
      apply();
    };
    window.addEventListener('resize', onResize);

    // Signal 2: VisualViewport (catches iOS adjustNothing where window doesn't resize)
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    const onVVResize = () => {
      if (!vv) return;
      const kbHeight = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      // Use whichever signal reports the larger keyboard
      if (kbHeight > 100 && kbHeight > lastHeight) {
        lastHeight = kbHeight;
        viewportOpen = true;
        apply();
      }
    };
    if (vv) {
      vv.addEventListener('resize', onVVResize);
      vv.addEventListener('scroll', onVVResize);
      onVVResize();
    }

    // Signal 3: focusin/focusout — most reliable, fires immediately on input tap
    const onFocusIn = (e) => {
      const t = e.target;
      if (t && t.matches && t.matches(TEXT_INPUT_SELECTOR)) {
        focusOpen = true;
        apply();
      }
    };
    const onFocusOut = (e) => {
      const t = e.target;
      if (t && t.matches && t.matches(TEXT_INPUT_SELECTOR)) {
        setTimeout(() => {
          const active = document.activeElement;
          const stillFocused = active && active.matches && active.matches(TEXT_INPUT_SELECTOR);
          if (!stillFocused) {
            focusOpen = false;
            apply();
          }
        }, 120);
      }
    };
    document.addEventListener('focusin', onFocusIn, true);
    document.addEventListener('focusout', onFocusOut, true);

    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onOrientation);
      if (vv) {
        vv.removeEventListener('resize', onVVResize);
        vv.removeEventListener('scroll', onVVResize);
      }
      document.removeEventListener('focusin', onFocusIn, true);
      document.removeEventListener('focusout', onFocusOut, true);
      root.style.setProperty('--kb-h', '0px');
      root.classList.remove('kb-open');
    };
  }, []);

  return state;
}
